export type GameId = '501' | 'around-the-clock' | 'cricket' | 'killer' | 'general';

export type Leg501Start = 501 | 301;

export type GameScoreContext = {
  title: string;
};

export type ScoreBounds = {
  min: number;
  max: number;
};

const CUMULATIVE_MAX = 999;

/** Infer game mode from session title (seed titles include keywords). */
export function resolveGameId(title: string): GameId {
  const t = title.toLowerCase();
  if (t.includes('501') || t.includes('301')) return '501';
  if (t.includes('around the clock') || t.includes('around-the-clock')) return 'around-the-clock';
  if (t.includes('cricket')) return 'cricket';
  if (t.includes('killer') || t.includes('bullseye') || t.includes('bonanza')) return 'killer';
  return 'general';
}

export function leg501FromTitle(title: string): Leg501Start {
  return title.toLowerCase().includes('301') ? 301 : 501;
}

/** Opening score on the board when a session is created. */
export function startingScoreForGame(gameId: GameId, ctx: GameScoreContext): number {
  switch (gameId) {
    case '501':
      return leg501FromTitle(ctx.title);
    case 'killer':
      return 3;
    case 'around-the-clock':
    case 'cricket':
    case 'general':
      return 0;
  }
}

/** Allowed score range while a session is live (inclusive). */
export function scoreBoundsForGame(gameId: GameId, ctx: GameScoreContext): ScoreBounds {
  switch (gameId) {
    case '501':
      return { min: 0, max: leg501FromTitle(ctx.title) };
    case 'killer':
      return { min: 0, max: 3 };
    case 'around-the-clock':
      return { min: 0, max: 21 };
    case 'cricket':
    case 'general':
      return { min: 0, max: CUMULATIVE_MAX };
  }
}

export function scoreBoundsForTitle(title: string): ScoreBounds {
  return scoreBoundsForGame(resolveGameId(title), { title });
}

export function clampScore(value: number, bounds: ScoreBounds): number {
  return Math.min(bounds.max, Math.max(bounds.min, value));
}

export function applyScoreChange(
  current: number,
  change: { delta?: number; set?: number },
  bounds: ScoreBounds,
): { ok: true; next: number; delta: number } | { ok: false; error: string } {
  const next = change.set ?? current + (change.delta ?? 0);
  if (!Number.isInteger(next)) return { ok: false, error: 'Score must be an integer' };
  if (next < bounds.min || next > bounds.max) {
    return { ok: false, error: `Score must be between ${bounds.min} and ${bounds.max}` };
  }
  return { ok: true, next, delta: next - current };
}
